import { c as _c } from "react/compiler-runtime";
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useShowroomLocator from 'hooks/useShowroomLocator';
import useRetailStores from 'hooks/useRetailStores';
import useZipCodeFromUser from 'global-state/zipCodeFromUser/useZipCodeFromUser';
import AdminPlaceholder from 'components/consumer/AdminPlaceholder';
import StoreLocatorSearch from './StoreLocatorSearch';
import StoreLocatorResultsList from './StoreLocatorResultsList';
import StoreLocatorViewAll from './StoreLocatorViewAll';
import StoreLocatorNoResults from './StoreLocatorNoResults';
import StoreLocatorMap from './StoreLocatorMap';
import { toStoreEntry } from './helpers';
const VirtualShowroomBlock = () => {
  const $ = _c(1);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = <div className="mt-6 pt-4 border-t border-gray-200 text-sm text-gray-700"><div className="text-base text-brand font-semibold mb-1">Virtual Showroom</div><div>Cannot go to a showroom?</div><div>Open the chat during business hour</div><div className="mt-1">or</div><a href="/showrooms/virtual/" className="text-brand underline hover:text-gray-800">Book a Virtual Showroom Appointment</a></div>;
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  return t0;
};
const StoreLocatorPage = t0 => {
  const $ = _c(43);
  const {
    isAdmin: t1
  } = t0;
  const isAdmin = t1 === undefined ? false : t1;
  const storedZip = useZipCodeFromUser();
  let t2;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = {
      limit: 50
    };
    $[0] = t2;
  } else {
    t2 = $[0];
  }
  const {
    stores: searchStores,
    loading: searchLoading,
    error: searchError,
    hasSearched,
    handleSearch,
    handleViewAll
  } = useShowroomLocator(t2);
  const {
    stores: allStores,
    loading: allStoresLoading,
    error: allStoresError
  } = useRetailStores();
  const [mode, setMode] = useState("viewAll");
  const autoSearchTriggeredRef = useRef(false);
  const [activeStoreId, setActiveStoreId] = useState(null);
  const [zipcode, setZipcode] = useState("");
  let t3;
  let t4;
  if ($[1] !== handleSearch || $[2] !== hasSearched || $[3] !== storedZip) {
    t3 = () => {
      if (!autoSearchTriggeredRef.current && storedZip && !hasSearched) {
        setZipcode(storedZip);
        handleSearch(storedZip);
        autoSearchTriggeredRef.current = true;
        setMode("search");
      }
    };
    t4 = [storedZip, hasSearched, handleSearch];
    $[1] = handleSearch;
    $[2] = hasSearched;
    $[3] = storedZip;
    $[4] = t3;
    $[5] = t4;
  } else {
    t3 = $[4];
    t4 = $[5];
  }
  useEffect(t3, t4);
  let t5;
  bb0: {
    if (mode === "viewAll") {
      let t6;
      if ($[6] !== allStores) {
        t6 = allStores || [];
        $[6] = allStores;
        $[7] = t6;
      } else {
        t6 = $[7];
      }
      t5 = t6;
      break bb0;
    }
    let t6;
    if ($[8] !== searchStores) {
      t6 = (searchStores || []).map(toStoreEntry).filter(Boolean).map(_temp);
      $[8] = searchStores;
      $[9] = t6;
    } else {
      t6 = $[9];
    }
    t5 = t6;
  }
  const mapStores = t5;
  let t6;
  if ($[10] !== handleSearch) {
    t6 = nextZip => {
      setZipcode(nextZip);
      handleSearch(nextZip);
      setMode("search");
      setActiveStoreId(null);
    };
    $[10] = handleSearch;
    $[11] = t6;
  } else {
    t6 = $[11];
  }
  const onSearch = t6;
  let t7;
  if ($[12] !== handleViewAll) {
    t7 = () => {
      setZipcode("");
      handleViewAll();
      setMode("viewAll");
      setActiveStoreId(null);
    };
    $[12] = handleViewAll;
    $[13] = t7;
  } else {
    t7 = $[13];
  }
  const onViewAll = t7;
  if (isAdmin) {
    let t8;
    if ($[14] === Symbol.for("react.memo_cache_sentinel")) {
      t8 = <AdminPlaceholder className="!min-h-[470px]">Store Locator Page Admin Placeholder</AdminPlaceholder>;
      $[14] = t8;
    } else {
      t8 = $[14];
    }
    return t8;
  }
  let t8;
  if ($[15] !== allStores || $[16] !== allStoresError || $[17] !== allStoresLoading || $[18] !== hasSearched || $[19] !== mode || $[20] !== searchError || $[21] !== searchLoading || $[22] !== searchStores) {
    t8 = () => {
      if (mode === "viewAll") {
        if (allStoresLoading) {
          return <div className="py-4 text-sm text-gray-600">Loading showrooms...</div>;
        }
        if (allStoresError) {
          return <div className="py-4 text-sm text-gray-600">Unable to load showrooms right now.</div>;
        }
        return <StoreLocatorViewAll stores={allStores} onActivate={setActiveStoreId} />;
      }
      if (searchLoading) {
        return <div className="py-4 text-sm text-gray-600">Searching...</div>;
      }
      if (searchError) {
        return <div className="py-4 text-sm text-gray-600">Unable to search right now. Please try again.</div>;
      }
      if (!hasSearched) {
        return null;
      }
      if (!searchStores || searchStores.length === 0) {
        return <StoreLocatorNoResults />;
      }
      return <StoreLocatorResultsList stores={searchStores} onActivate={setActiveStoreId} />;
    };
    $[15] = allStores;
    $[16] = allStoresError;
    $[17] = allStoresLoading;
    $[18] = hasSearched;
    $[19] = mode;
    $[20] = searchError;
    $[21] = searchLoading;
    $[22] = searchStores;
    $[23] = t8;
  } else {
    t8 = $[23];
  }
  const renderList = t8;
  const hasMapContent = mapStores && mapStores.length > 0;
  let t9;
  if ($[24] !== onSearch || $[25] !== onViewAll || $[26] !== zipcode) {
    t9 = <StoreLocatorSearch zipcode={zipcode} onZipcodeChange={setZipcode} onSearch={onSearch} onViewAll={onViewAll} />;
    $[24] = onSearch;
    $[25] = onViewAll;
    $[26] = zipcode;
    $[27] = t9;
  } else {
    t9 = $[27];
  }
  let t10;
  if ($[28] !== renderList) {
    t10 = renderList();
    $[28] = renderList;
    $[29] = t10;
  } else {
    t10 = $[29];
  }
  let t11;
  if ($[30] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <VirtualShowroomBlock />;
    $[30] = t11;
  } else {
    t11 = $[30];
  }
  let t12;
  if ($[31] !== t10) {
    t12 = <aside className="w-full lg:w-80 lg:flex-shrink-0 px-4 py-4 lg:overflow-y-auto">{t10}{t11}</aside>;
    $[31] = t10;
    $[32] = t12;
  } else {
    t12 = $[32];
  }
  let t13;
  if ($[33] !== activeStoreId || $[34] !== hasMapContent || $[35] !== mapStores) {
    t13 = <main className="flex-1 h-96 lg:h-full">{hasMapContent ? <StoreLocatorMap stores={mapStores} activeStoreId={activeStoreId} onMarkerClick={setActiveStoreId} /> : <div className="w-full h-full bg-gray-100" />}</main>;
    $[33] = activeStoreId;
    $[34] = hasMapContent;
    $[35] = mapStores;
    $[36] = t13;
  } else {
    t13 = $[36];
  }
  let t14;
  if ($[37] !== t12 || $[38] !== t13) {
    t14 = <div className="w-full flex flex-col lg:flex-row lg:flex-1 lg:min-h-0">{t12}{t13}</div>;
    $[37] = t12;
    $[38] = t13;
    $[39] = t14;
  } else {
    t14 = $[39];
  }
  let t15;
  if ($[40] !== t14 || $[41] !== t9) {
    t15 = <div className="w-full flex flex-col lg:h-screen">{t9}{t14}</div>;
    $[40] = t14;
    $[41] = t9;
    $[42] = t15;
  } else {
    t15 = $[42];
  }
  return t15;
};
StoreLocatorPage.propTypes = {
  isAdmin: PropTypes.bool
};
export default StoreLocatorPage;
function _temp(entry) {
  return entry.store;
}